import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { updateSocialMeta } from '../utils/seo';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Tv, Beer, Music } from 'lucide-react';

const SPORTSBAR_PAGE_TITLE =
  'Sports Bar & Lounge - Pin High Golf & Lounge | Watch Sports & Live Music Bay Village';
const SPORTSBAR_META_DESCRIPTION =
  "Pin High in Bay Village is a sports bar and lounge on Cleveland's Westside—big screens for the game, full bar and food, and live music in summer. More than indoor golf simulators.";

const Sportsbar = () => {
  useEffect(() => {
    document.title = SPORTSBAR_PAGE_TITLE;
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', SPORTSBAR_META_DESCRIPTION);
    }
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', 'https://pinhighbay.com/sportsbar');
    updateSocialMeta({
      title: SPORTSBAR_PAGE_TITLE,
      description: SPORTSBAR_META_DESCRIPTION,
      path: '/sportsbar',
    });

    const webPageSchema = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': 'https://pinhighbay.com/sportsbar#webpage',
      url: 'https://pinhighbay.com/sportsbar',
      name: SPORTSBAR_PAGE_TITLE,
      description: SPORTSBAR_META_DESCRIPTION,
      isPartOf: {
        '@type': 'WebSite',
        name: 'Pin High Golf & Lounge',
        url: 'https://pinhighbay.com',
      },
      about: {
        '@type': 'SportsActivityLocation',
        '@id': 'https://pinhighbay.com/#organization',
        name: 'Pin High Golf & Lounge',
      },
      primaryImageOfPage: {
        '@type': 'ImageObject',
        url: 'https://pinhighbay.com/home-hero.jpg',
      },
    };

    let schemaScript = document.querySelector('#sportsbar-webpage-schema');
    if (!schemaScript) {
      schemaScript = document.createElement('script');
      schemaScript.id = 'sportsbar-webpage-schema';
      schemaScript.type = 'application/ld+json';
      document.head.appendChild(schemaScript);
    }
    schemaScript.textContent = JSON.stringify(webPageSchema);

    return () => {
      const existing = document.querySelector('#sportsbar-webpage-schema');
      if (existing) existing.remove();
    };
  }, []);

  const highlights = [
    {
      icon: Tv,
      title: 'Watch the game',
      description:
        'Catch your favorite teams on our screens in a comfortable lounge atmosphere. Whether it is the big game or a casual afternoon, Pin High is a great spot to settle in and cheer with friends.',
    },
    {
      icon: Beer,
      title: 'Drinks at the bar',
      description:
        'Grab a cold beer, wine, or a mixed drink from our full bar. Pair it with food from our menu—pizza, snacks, and more—without ever leaving your seat.',
    },
    {
      icon: Music,
      title: 'Live music in summer',
      description:
        'During the summer months, we bring live music to the lounge. Enjoy local talent, good company, and the same welcoming vibe that makes Pin High a neighborhood favorite.',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <section className="relative h-[70vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="/home-hero.jpg"
            alt="Pin High Golf & Lounge bar and lounge in Bay Village"
            className="w-full h-full object-cover opacity-90"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/55 to-black/35" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <Tv className="w-16 h-16 mx-auto mb-6 text-green-400" />
          <h1 className="text-5xl md:text-7xl font-bold mb-6 tracking-tight">
            More Than a Simulator
          </h1>
          <p className="text-xl md:text-2xl mb-6 max-w-3xl mx-auto text-gray-200">
            Pin High is a full lounge and sports bar experience—perfect for watching the game, unwinding with a drink, and soaking up live music when the weather warms up.
          </p>
          <p className="text-lg md:text-xl max-w-2xl mx-auto text-gray-300 mb-8">
            You do not have to tee it up to have a great time. Come for the atmosphere, stay for the company.
          </p>
          <Button
            asChild
            size="lg"
            className="bg-green-600 hover:bg-green-700 text-white text-lg px-8 py-6"
          >
            <Link to="/menu">View Menu</Link>
          </Button>
        </div>
      </section>

      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900">
              Lounge, bar &amp; good times
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our Bay Village location on the Westside of Cleveland is built for golfers and non-golfers alike—big screens, a full bar, and seasonal live entertainment.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {highlights.map(({ icon: Icon, title, description }) => (
              <Card key={title} className="border-none shadow-lg hover:shadow-xl transition-shadow duration-300">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 bg-green-600 rounded-lg flex items-center justify-center mx-auto mb-6">
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{title}</h3>
                  <p className="text-gray-600 leading-relaxed">{description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900">
            Plan your visit
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            Stop by for drinks and the game, or pair your visit with simulator time. Book a bay anytime—or just walk in when you are in the neighborhood.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="bg-green-600 hover:bg-green-700 text-white">
              <a
                href="https://app.whoosh.io/patron/club/pin-high-golf-lounge/agenda/pin-high/today"
                target="_blank"
                rel="noopener noreferrer"
              >
                Book a bay
              </a>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-green-600 text-green-600 hover:bg-green-50">
              <Link to="/contact">Contact us</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Sportsbar;
